import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  Badge,
  Button,
  ErrorState,
  FieldSelect,
  Icon,
  LoadingState,
  PageHeader,
  SectionHeader,
  TextField,
} from '../../../group_leader/components/common'
import { makeT, sharedT, translate } from '../../../group_leader/helpers/i18n'
import { isPresent } from '../../../group_leader/helpers/value'
import {
  buildCustomPanelFields,
  buildPanelFields,
  eventHolesNodeId,
  extractChoices,
  fallbackPlaceLabels,
  getPresetConflict,
  optSummary,
  pillMeta,
  presetInitials,
  presetNodeSelections,
  presetForSpec,
  specDisplayName,
  saveNodeSelections,
  trimTrailingZeros,
  collectFieldValues,
  fieldValueFor,
  buildPayoutForSpec,
  isFieldFilled,
  normalizeLookup,
  isNodeSelectionKey,
  isProRataPointsPayout,
  offersPerPlayerFee,
  resolvedFormatName,
  resolvedNodeSelections,
  formatMoney,
} from '../../helpers/games'
import {
  useListRoundsQuery,
  useListPresetsQuery,
  useGetSpecFormContextQuery,
  useGetValidMagicNetworkNodesQuery,
  useListTournamentSpecsQuery,
  useListUserTournamentSpecsQuery,
  useCreateTournamentSpecMutation,
  useUpdateTournamentSpecMutation,
  useDeleteTournamentSpecMutation,
  useEnrollRosterInSkinsMutation,
  useCheckRegimeMutation,
  useCreateUserScoredSpecMutation,
  useUpdateUserScoredSpecMutation,
  useDeleteUserScoredSpecMutation,
} from '../../../group_leader/store/glApi'
import { showErrorNotification } from '../../../shared/helpers'
import ConfigPanelHead from '../common/ConfigPanelHead'
import DisclosureButton from '../common/DisclosureButton'
import PayoutTable from '../common/PayoutTable'
import SpecConfigFooter from '../common/SpecConfigFooter'
import WizardFooter from '../common/WizardFooter'
import { goToNextStep, markStepCompleted, setRoundId, setGamesFormatFilter } from '../../store/wizardSlice'

const PAGE_SIZE = 10

const t = makeT('group_leader.event_setup.games')

const ADVANCED_FIELDS_ID = 'gl-event-setup-game-fields'

// Fresh each time, so a reset never shares one array between the payout and points rows.
const blankRows = () => [ '', '', '' ]

const isNoneChoice = (name) => String(name).trim()
  .toLowerCase() === 'none'

// An added game with no configuration of its own: an add-on, or a spec restored without one.
const blankGame = (game) => ({
  fieldSelections: {}, resolvedOptions: {}, payoutTypeId: '', payoutRows: blankRows(), pointsRows: blankRows(), ...game,
})

const ConfigFields = ({ fields, idPrefix, getValue, onChange, id, showAdvanced }) => {
  // Display only: the advanced rows stay in `fields` so the positional payloads keep their shape.
  const shown = showAdvanced ? fields : fields.filter((field) => !field.advanced)
  const selects = shown.filter((field) => Array.isArray(field.choices))
  const allowances = shown.filter((field) => field.type === 'percent')
  const points = shown.filter((field) => field.type === 'points')
  const balls = shown.filter((field) => field.type === 'balls')
  const numbers = shown.filter((field) => field.type === 'attr_number')
  const headingId = `${idPrefix}-allowance-heading`
  const numberField = (field, props) => (
    <TextField
      key={field.key}
      id={`${idPrefix}-${field.key}`}
      type="number"
      step="1"
      value={getValue(field.key) ?? ''}
      onChange={(value) => onChange(field.key, value)}
      {...props}
    />
  )
  // Points and balls each keep a visible label per row: "Eagle" and "Birdie" are not interchangeable.
  const countGroup = (rows, group, heading, rowClass) => rows.length > 0 && (
    <div className="gl-event-setup-points-group" role="group" aria-labelledby={`${idPrefix}-${group}-heading`}>
      <div className="gl-event-setup-points-heading gl-field-label" id={`${idPrefix}-${group}-heading`}>{heading}</div>
      <div className={rowClass}>
        {rows.map((field) => numberField(field, {
          label: field.label, className: 'gl-event-setup-points-cell', min: group === 'balls' ? '0' : undefined,
        }))}
      </div>
    </div>
  )

  return (
    <div className="gl-event-setup-sc-fields" id={id}>
      {selects.map((field) => (
        <FieldSelect
          key={field.key}
          id={`${idPrefix}-${field.key}`}
          label={field.label}
          options={field.choices}
          value={getValue(field.key) ?? ''}
          onChange={(value) => onChange(field.key, value)}
          placeholder={t('config.select_placeholder')}
        />
      ))}
      {numbers.map((field) => numberField(field, { label: field.label }))}
      {allowances.length === 1 && numberField(allowances[0], { label: allowances[0].label, min: '1', suffix: '%' })}
      {allowances.length > 1 && (
        <div className="gl-event-setup-allowance-group" role="group" aria-labelledby={headingId}>
          <div className="gl-event-setup-allowance-heading gl-field-label" id={headingId}>{t('fields.handicap_allowance')}</div>
          <div className="gl-event-setup-allowance-row">
            {/* Inline prefixes line these up with the fields to the left; ariaLabel names each (RTM-10281). */}
            {allowances.map((field, idx) => numberField(field, {
              ariaLabel: field.label,
              prefix: String.fromCharCode(65 + idx),
              className: 'gl-event-setup-allowance-cell',
              min: '1',
              suffix: '%',
            }))}
          </div>
        </div>
      )}
      {/* The magic network's own label, so the wizard and the classic spec form read the same. */}
      {countGroup(points, 'points', translate('models.level.labels.lookup_fields'), 'gl-event-setup-points-row')}
      {/* Par or hole counts (RTM-10487), stacked: each row reads "Par 4 -> how many balls count". */}
      {balls.length > 0 && countGroup(balls, 'balls', balls[0].heading, 'gl-event-setup-balls-stack')}
    </div>
  )
}

ConfigFields.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  idPrefix: PropTypes.string.isRequired,
  getValue: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string,
  showAdvanced: PropTypes.bool,
}

const GamesStep = () => {
  const dispatch = useDispatch()
  const leagueId = useSelector((s) => s.misc.data.leagueId)
  // The league's own symbol, not a locale's: a UK club runs an English event in pounds.
  const currencySymbol = useSelector((s) => s.misc.data.currencySymbol) || '$'
  const wizardRoundId = useSelector((s) => s.wizard.roundId)
  const formatFilter = useSelector((s) => s.wizard.gamesFormatFilter)

  const [ selectedPreset, setSelectedPreset ] = useState(null)
  const [ fieldSelections, setFieldSelections ] = useState({})
  const [ payoutTypeId, setPayoutTypeId ] = useState('')
  const [ payoutRows, setPayoutRows ] = useState(blankRows)
  const [ pointsRows, setPointsRows ] = useState(blankRows)

  // 'fixed' = the amount is the total pot; 'dynamic' = a per-player fee multiplied by the field (RTM-10087).
  const [ pointsPurseMode, setPointsPurseMode ] = useState('fixed')
  const [ customGameMode, setCustomGameMode ] = useState(false)
  const [ advancedOpen, setAdvancedOpen ] = useState(false)
  const [ customGameName, setCustomGameName ] = useState('')
  // Sent as int_name so any game can be renamed; skins keep theirs, as enrolment matches them by it.
  const [ gameName, setGameName ] = useState('')
  const [ addedGames, setAddedGames ] = useState([])
  const [ pageIdx, setPageIdx ] = useState(0)
  const [ editingSpecId, setEditingSpecId ] = useState(null)
  // Specs whose DELETE is in flight; the delete mutations expose no loading flag of their own.
  const [ removingIds, setRemovingIds ] = useState([])

  const initializedRef = useRef(false)
  const editSetupRef = useRef(false)
  const panelRef = useRef(null)

  const resetPayoutRows = () => { setPayoutRows(blankRows()); setPointsRows(blankRows()) }
  const resetPayout = (purseMode) => { setPayoutTypeId(''); resetPayoutRows(); setPointsPurseMode(purseMode) }

  const { data: roundsData } = useListRoundsQuery(leagueId, { skip: !leagueId })
  const roundId = wizardRoundId || roundsData?.data?.[0]?.id || null

  // Walk-on / no tee sheet is group size 1 server-side, and the server refuses a team
  // competition on it. Read from the round, as the later steps do, so it survives a reload.
  const round = roundsData?.data?.find((entry) => String(entry.id) === String(roundId))
    || roundsData?.data?.[0]
  const isWalkon = String(round?.attributes?.pairing_group_size) === '1'

  // Publishes the round the wizard should use as soon as one is known.
  useEffect(() => { if (!wizardRoundId && roundId) { dispatch(setRoundId(roundId)) } }, [ roundId ])

  const { data: presetsData, isLoading: presetsLoading, error: presetsError } =
    useListPresetsQuery(leagueId, { skip: !leagueId })

  const presets = presetsData?.presets || (Array.isArray(presetsData) ? presetsData : [])

  // A failed list would otherwise read as "no games yet", which invites a duplicate.
  const { data: specsData, error: specsError } = useListTournamentSpecsQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId },
  )

  const { data: userSpecsData, error: userSpecsError } = useListUserTournamentSpecsQuery(
    { leagueId, roundId },
    { skip: !leagueId || !roundId },
  )

  const { data: formCtxData } = useGetSpecFormContextQuery(
    { leagueId, roundId: roundId || '', intName: selectedPreset?.id || '' },
    { skip: !selectedPreset || !roundId },
  )

  const isSkins = selectedPreset?.kind === 'skins'
  const isUserScored = selectedPreset?.kind === 'user_scored'

  // While open, Edit/Delete on saved games are locked so the game in progress is finished first.
  const panelOpen = Boolean(selectedPreset) || customGameMode

  // Not from `panelFields`, which this request's response builds: the panel would be its own input.
  const eventHolesId = eventHolesNodeId(addedGames)

  const fieldNodeSelections = selectedPreset ? presetNodeSelections(selectedPreset) : {}
  if (selectedPreset && isPresent(eventHolesId) && !selectedPreset.holes_node_ids?.length) {
    fieldNodeSelections.holes = Number(eventHolesId)
  }
  if (selectedPreset || customGameMode) {
    Object.entries(fieldSelections).forEach(([ k, v ]) => {
      if (isPresent(v) && isNodeSelectionKey(k)) { fieldNodeSelections[k] = Number(v) }
    })
  }
  // Skins need by_team:group (4), or by_team's default "no" edges competition 58 (Player v. Group) out.
  if (isSkins) { fieldNodeSelections.by_team = 4 }

  // `resolvingNodes` holds the save: the saved map comes from this response, so it must match the last pick.
  const { data: validNodesData, isFetching: resolvingNodes } = useGetValidMagicNetworkNodesQuery(
    {
      leagueId,
      roundId,
      // The server curates formats for this context: no flights, Drive-Chip-Putt or triples (RTM-10156).
      context: 'group_event',
      selections: {
        ...fieldNodeSelections,
        ...(payoutTypeId ? { payout: Number(payoutTypeId) } : {}),
      },
    },
    { skip: (!selectedPreset && !customGameMode) || !roundId },
  )

  const customPanelFields = (customGameMode || advancedOpen)
    ? buildCustomPanelFields(validNodesData, {
      quotaType: fieldSelections.quota_type,
      includeQuota: customGameMode,
      preset: selectedPreset,
    })
    : []
  // Advanced swaps in the full list; both are keyed the same, so the leader's values survive the swap.
  const panelFields = advancedOpen
    ? customPanelFields
    : buildPanelFields(selectedPreset, formCtxData, validNodesData, eventHolesId)

  const getFieldValue = (key) => fieldValueFor(panelFields, fieldSelections, key)

  const rawPayoutNodeChoices = validNodesData?.payout?.nodes?.filter((n) => n.kind !== 'header') || []
  const payoutChoices = rawPayoutNodeChoices.length > 0
    ? rawPayoutNodeChoices
    : extractChoices(formCtxData, 'payout') || []

  // None beside the general schemes, else the format's own (by Nassau, the Skins trio); never blank.
  const defaultPayoutId = (choices) => {
    const none = choices.find((c) => isNoneChoice(c.name))
    const general = choices.some((c) => /winner takes all/i.test(String(c.name)))
    if (general && none) { return none }

    return choices.find((c) => c.selected) || none || choices[0]
  }

  // Shown until the effect stores it, so the select does not flick off the first option.
  const resolvedPayoutTypeId = payoutTypeId || String(defaultPayoutId(payoutChoices)?.id ?? '')

  // Unset would take the preset's default server-side, and a Nassau default with no amounts breaks the leaderboard.
  const explicitNoPayoutId = () => {
    const entered = [ payoutRows, pointsRows ]
    if (entered.some((rows) => trimTrailingZeros(rows).length > 0)) { return null }
    return payoutChoices.find((c) => isNoneChoice(c.name))?.id ?? null
  }

  const applyPayoutSelection = (nodeSelections) => {
    const payoutId = payoutTypeId || explicitNoPayoutId()
    if (payoutId) { nodeSelections.payout = Number(payoutId) }
  }

  const getCustomFieldValue = (key) => fieldValueFor(customPanelFields, fieldSelections, key)

  const _payoutFieldsDef = payoutTypeId ? validNodesData?.payout_fields : null
  const proRataPoints = isProRataPointsPayout(payoutChoices, payoutTypeId)
  const payoutHideInputs = !payoutTypeId || (_payoutFieldsDef?.visible === false && !proRataPoints)
  const _serverPayoutLabels = _payoutFieldsDef?.fields?.map((f) => f.label)
  const payoutFieldLabels = (_serverPayoutLabels?.length && _serverPayoutLabels)
    || (proRataPoints ? [ t('config.total_purse') ] : fallbackPlaceLabels())
  const isExpandable = payoutFieldLabels.length >= 10
  const perPlayerFeeOffered = offersPerPlayerFee(payoutChoices, resolvedPayoutTypeId)

  const [ createTournamentSpec, { isLoading: creating } ] = useCreateTournamentSpecMutation()
  const [ updateTournamentSpec, { isLoading: updating } ] = useUpdateTournamentSpecMutation()
  const [ deleteTournamentSpec ] = useDeleteTournamentSpecMutation()
  const [ enrollRosterInSkins ] = useEnrollRosterInSkinsMutation()
  const [ checkRegime, { isLoading: checking } ] = useCheckRegimeMutation()
  const [ createUserScoredSpec, { isLoading: savingAddOn } ] = useCreateUserScoredSpecMutation()
  const [ updateUserScoredSpec, { isLoading: renamingAddOn } ] = useUpdateUserScoredSpecMutation()
  const [ deleteUserScoredSpec ] = useDeleteUserScoredSpecMutation()

  // tms_api answers {error: "..."}, the monolith relay {errors: ["..."]}; read either.
  const apiError = (result) => result?.error?.data?.error || result?.error?.data?.errors?.[0]

  useEffect(() => {
    // A failed add-ons list still restores the main specs, the half that stops a game being added twice.
    if (initializedRef.current || !specsData || (!userSpecsData && !userSpecsError)
      || !presets.length) { return }
    initializedRef.current = true
    const existing = specsData?.data || []
    const userScored = userSpecsData?.data || []
    const restoredSpecs = existing.map((spec) => {
      const intName = spec.name || ''
      const specId = spec.id
      // Node selections first, name as fallback: a renamed game keeps its preset and the leader's name.
      const matched = presetForSpec(presets, spec.node_selections)
        || presets.find((p) => p.id === intName || p.name === intName)
      const preset = matched
        ? { ...matched, name: intName || matched.name }
        : { id: intName, name: specDisplayName(intName), options: {} }
      const payoutArr = spec.payout || []
      const ptsArr = spec.points || []
      const restoredPayout = payoutArr.length > 0 ? payoutArr.map(String) : blankRows()
      const restoredPoints = ptsArr.length > 0 ? ptsArr.map(String) : blankRows()
      // One add/remove control drives both, so they restore to the same length.
      const padTo = (rows, size) => (rows.length >= size ? rows : [ ...rows, ...Array(size - rows.length).fill('') ])
      const placeCount = Math.max(restoredPayout.length, restoredPoints.length)
      const ns = spec.node_selections || {}
      const restoredFieldSelections = {}
      const restoredResolvedOptions = {}
      Object.entries(ns).forEach(([ k, v ]) => {
        if (v?.id && k !== 'payout') {
          restoredFieldSelections[k] = String(v.id)
          restoredResolvedOptions[k] = v
        }
      })
      const restoredPayoutTypeId = ns.payout?.id ? String(ns.payout.id) : ''
      return {
        preset,
        specId,
        fieldSelections: restoredFieldSelections,
        resolvedOptions: restoredResolvedOptions,
        payoutTypeId: restoredPayoutTypeId,
        payoutRows: padTo(restoredPayout, placeCount),
        pointsRows: padTo(restoredPoints, placeCount),
        pointsPurseMode: spec.points_purse_mode || 'fixed',
        handicapAdjustment: spec.handicap_adjustment || [],
        lookup: normalizeLookup(spec.lookup),
        specialHoles: normalizeLookup(spec.special_holes),
        specAttrs: {
          ...(isPresent(spec.quota_type) ? { quota_type: spec.quota_type } : {}),
          ...(isPresent(spec.starting_quota) ? { starting_quota: spec.starting_quota } : {}),
        },
      }
    })
    const restoredUserSpecs = userScored.map((spec) => {
      const specName = spec.name || t('user_scored_fallback')
      const matched = presets.find((p) => p.kind === 'user_scored' && p.name === specName)
      const preset = matched || { id: specName, name: specName, kind: 'user_scored', options: {} }
      return blankGame({ preset, specId: spec.id, pointsPurseMode: spec.points_purse_mode || 'fixed' })
    })
    const all = [ ...restoredSpecs, ...restoredUserSpecs ]
    if (!all.length) { return }
    setAddedGames(all)
  }, [ specsData, userSpecsData, presets ])

  useEffect(() => {
    setAdvancedOpen(false)
    if (editSetupRef.current) { editSetupRef.current = false; return }
    setFieldSelections({})
    // Skins are bought into per golfer, so the amount a leader types is a per-player fee.
    resetPayout(isSkins ? 'dynamic' : 'fixed')
  }, [ selectedPreset?.id ])

  useEffect(() => {
    const choices = rawPayoutNodeChoices
    if (!choices.length) { return }
    const stillOffered = payoutTypeId && choices.some((c) => String(c.id) === payoutTypeId)
    if (stillOffered) { return }
    if (payoutTypeId) { resetPayoutRows() }
    const fallback = defaultPayoutId(choices)
    setPayoutTypeId(fallback ? String(fallback.id) : '')
  }, [ validNodesData ])

  const allFilled = panelFields.every((f) => isFieldFilled(f, getFieldValue(f.key)))
  // Length first: an empty panel is the traversal in flight, and [].every() would save no selections.
  const customAllFilled = customPanelFields.length > 0
    && customPanelFields.every((f) => isFieldFilled(f, getCustomFieldValue(f.key)))
  const customUnfilledLabels = customPanelFields
    .filter((f) => !isFieldFilled(f, getCustomFieldValue(f.key)))
    .map((f) => f.label)
    .join(', ')
  // No name needed: a blank field falls back to the preset's own name on save.
  const canAddGame = (isUserScored || allFilled) && !creating && !updating && !checking && !resolvingNodes
    && !savingAddOn && !renamingAddOn

  const addedIds = new Set(addedGames.map((g) => g.preset.id))
  // The user-scored add-ons are repeatable, so only one-per-event formats block their card.
  const blockedIds = new Set(addedGames.filter((g) => g.preset.kind !== 'user_scored')
    .map((g) => g.preset.id))

  const filteredPresets = presets.filter((p) => {
    if (p.kind === 'skins' || p.kind === 'user_scored') { return formatFilter === 'individual' }
    return p.group === formatFilter
  })

  useEffect(() => { setPageIdx(0) }, [ formatFilter ])

  // Redux does not survive a reload, so each step reports completion from restored data.
  useEffect(() => {
    if (addedGames.length > 0) { dispatch(markStepCompleted('games')) }
  }, [ addedGames.length ])

  const pageStart = pageIdx * PAGE_SIZE
  const paginated = filteredPresets.slice(pageStart, pageStart + PAGE_SIZE)
  const canPrev = pageIdx > 0
  const canNext = pageStart + PAGE_SIZE < filteredPresets.length

  const resetCustomGamePanel = () => {
    // Editing a custom game leaves `selectedPreset` null, so the effect never clears this flag itself.
    editSetupRef.current = false
    setCustomGameMode(false)
    setCustomGameName('')
    setGameName('')
    setEditingSpecId(null)
    setFieldSelections({})
    resetPayout('fixed')
  }

  const handleCardClick = (preset) => {
    if (blockedIds.has(preset.id)) { return }
    if (getPresetConflict(preset, addedGames, { isWalkon })) { return }
    resetCustomGamePanel()
    // Every game opens its panel, the add-ons included (just the name), seeded with the preset's name.
    setGameName(preset.name)
    setSelectedPreset((prev) => (prev?.id === preset.id ? null : preset))
  }

  const handleEditGame = (game) => {
    editSetupRef.current = true
    setEditingSpecId(game.specId)
    setGameName(game.preset.name)
    const matchedPreset = presets.find((p) => p.id === game.preset.id)
    if (matchedPreset) {
      setSelectedPreset(matchedPreset)
      setCustomGameMode(false)
      setCustomGameName('')
    } else if (game.preset?.kind === 'user_scored') {
      // A renamed add-on matches no preset after a reload, but must not fall through to the custom panel.
      setSelectedPreset(game.preset)
      setCustomGameMode(false)
      setCustomGameName('')
    } else {
      setCustomGameMode(true)
      setCustomGameName(game.preset.name)
      setSelectedPreset(null)
    }
    // The saved holes win over the event-wide default, which would rewrite a deliberate choice.
    const savedSelections = { ...(game.fieldSelections || {}) }
    const savedHoles = game.resolvedOptions?.holes?.id
    if (isPresent(savedHoles) && !isPresent(savedSelections.holes)) {
      savedSelections.holes = String(savedHoles)
    }
    // Positional spec fields, under the keys the panel builders use; the allowance also seeds its single form.
    const positional = { handicap_adjustment_: game.handicapAdjustment, lookup_: game.lookup, balls_: game.specialHoles }
    Object.entries(positional).forEach(([ prefix, values ]) => {
      (values || []).forEach((value, i) => { savedSelections[`${prefix}${i}`] = String(value) })
    })
    if (game.handicapAdjustment?.length) { savedSelections.handicap_adjustment = String(game.handicapAdjustment[0]) }
    // Quota's own spec columns, restored under the keys buildQuotaFields uses.
    Object.entries(game.specAttrs || {}).forEach(([ key, value ]) => {
      savedSelections[key] = String(value)
    })
    setFieldSelections(savedSelections)
    setPayoutTypeId(game.payoutTypeId || '')
    setPayoutRows(game.payoutRows?.length > 0 ? game.payoutRows : blankRows())
    setPointsRows(game.pointsRows?.length > 0 ? game.pointsRows : blankRows())
    setPointsPurseMode(game.pointsPurseMode || 'fixed')
  }

  // Edit disables itself as the panel opens, dropping focus to <body>; the custom panel autofocuses its name.
  useEffect(() => {
    if (selectedPreset) { panelRef.current?.focus() }
  }, [ selectedPreset?.id ])

  const handleFieldChange = (key, value) => {
    setFieldSelections((prev) => ({ ...prev, [key]: value }))
  }

  const handlePayoutTypeChange = (value) => {
    setPayoutTypeId(value)
    resetPayoutRows()
    // Back to the preset default: the new type may not offer the per-player choice.
    setPointsPurseMode(isSkins ? 'dynamic' : 'fixed')
  }

  const handleSaveUserScored = async ({ thenAdd = false } = {}) => {
    const name = gameName.trim() || selectedPreset.name
    // One call: a delete then create could destroy the game, events and award included, on a failure between.
    const result = editingSpecId
      ? await updateUserScoredSpec({ leagueId, roundId, specId: editingSpecId, name })
      : await createUserScoredSpec({ leagueId, roundId, name })
    if (result.error) {
      showErrorNotification(apiError(result)
        || t(editingSpecId ? 'errors.update_failed' : 'errors.add_failed'))
      return
    }
    // A rename keeps the spec, so the id is the one being edited.
    const saved = blankGame({ preset: { ...selectedPreset, name }, specId: editingSpecId || result.data?.id })
    setAddedGames((prev) => (editingSpecId
      ? prev.map((g) => (g.specId === editingSpecId ? saved : g))
      : [ ...prev, saved ]))
    setEditingSpecId(null)
    // "Add another" leaves the panel open as a fresh add of the same format (RTM-10977).
    if (thenAdd) { setGameName(selectedPreset.name); return }
    setSelectedPreset(null)
    setGameName('')
  }

  // Both save paths: collect, resolve the payout, check the regime, then save; null once it reported a failure.
  const persistSpec = async ({ fields, getValue, seed, intName, specFields, addFailedKey }) => {
    if (!roundId) {
      showErrorNotification(t('errors.round_not_found'))
      return null
    }
    const failedKey = editingSpecId ? 'errors.update_failed' : addFailedKey
    if (!validNodesData) {
      showErrorNotification(t(failedKey))
      return null
    }
    const collected = collectFieldValues(fields, getValue, seed)
    // The server's resolution of every level, then the leader's picks on top.
    const nodeSelections = { ...resolvedNodeSelections(validNodesData), ...collected.nodeSelections }
    applyPayoutSelection(nodeSelections)
    if (isSkins) { nodeSelections.by_team = 4 }

    // On update too: Advanced can change the format, and UpdateService runs no regime check.
    const regimeResult = await checkRegime({ leagueId, roundId, nodeSelections })
    if (regimeResult.error) {
      showErrorNotification(apiError(regimeResult) || t('errors.incompatible_format'))
      return null
    }

    const body = {
      leagueId,
      roundId,
      intName,
      nodeSelections,
      divisionIds: [],
      ...(collected.handicapAdjustment ? { handicapAdjustment: collected.handicapAdjustment } : {}),
      pointsPurseMode,
      ...buildPayoutForSpec({ payoutRows, pointsRows, payoutTypeId }),
      ...specFields(collected),
    }
    const result = editingSpecId
      ? await updateTournamentSpec({ ...body, specId: editingSpecId })
      : await createTournamentSpec(body)

    if (result.error) {
      // The API's own reason where it has one ("Too many handicap_adjustment entries") is actionable.
      showErrorNotification(apiError(result) || t(failedKey))
      return null
    }
    return { ...collected, result }
  }

  const handleAddGame = async () => {
    if (!canAddGame || !selectedPreset) { return }
    if (!roundId) {
      showErrorNotification(t('errors.round_not_found'))
      return
    }
    // The add-ons have only a name and their own relay endpoints.
    if (selectedPreset.kind === 'user_scored') { return handleSaveUserScored() }
    // Skins keep their canonical name, which the scoring engine matches by int_name.
    const effectiveName = isSkins ? selectedPreset.name : (gameName.trim() || selectedPreset.name)
    const conflict = getPresetConflict(selectedPreset, addedGames, { isWalkon })
    if (conflict) {
      showErrorNotification(conflict)
      return
    }

    // Preset nodes, then the panel's changes (RTM-10064); lookup rides every write or Stableford scores zero.
    let effectiveLookup
    let effectiveSpecialHoles
    const saved = await persistSpec({
      fields: panelFields,
      getValue: getFieldValue,
      seed: saveNodeSelections(selectedPreset, fieldSelections, validNodesData),
      intName: effectiveName,
      addFailedKey: 'errors.add_failed',
      specFields: ({ lookup, specialHoles }) => {
        // Preset columns seed a new game only: on update the API keeps what the body omits.
        effectiveLookup = lookup || (editingSpecId ? null : selectedPreset.lookup)
        effectiveSpecialHoles = specialHoles || (editingSpecId ? null : selectedPreset.special_holes)
        return {
          ...(effectiveLookup ? { lookup: effectiveLookup } : {}),
          ...(effectiveSpecialHoles ? { specialHoles: effectiveSpecialHoles } : {}),
        }
      },
    })
    if (!saved) { return }

    const { handicapAdjustment, result } = saved
    const ns = result.data?.node_selections || {}
    const resolvedOptions = {}
    panelFields.forEach((f) => { if (ns[f.key]?.id) { resolvedOptions[f.key] = ns[f.key] } })
    // Omitted when absent so the update's spread cannot clear what it deliberately left alone.
    const row = {
      fieldSelections, resolvedOptions, payoutTypeId, payoutRows, pointsRows, pointsPurseMode,
      handicapAdjustment,
      ...(effectiveLookup ? { lookup: effectiveLookup } : {}),
      ...(effectiveSpecialHoles ? { specialHoles: effectiveSpecialHoles } : {}),
    }

    if (editingSpecId) {
      setAddedGames((prev) => prev.map((g) =>
        (g.specId === editingSpecId
          ? { ...g, preset: { ...g.preset, name: effectiveName }, ...row }
          : g)))
      setEditingSpecId(null)
      setSelectedPreset(null)
      return
    }

    setAddedGames((prev) => [ ...prev,
      { preset: { ...selectedPreset, name: effectiveName }, specId: result.data?.id, ...row } ])
    setSelectedPreset(null)
    resetPayout('fixed')

    if (isSkins) { enrollRosterInSkins({ leagueId, roundId }) }
  }

  const handleRemoveGame = async (game) => {
    if (game.specId && roundId) {
      if (removingIds.includes(game.specId)) { return }
      setRemovingIds((prev) => [ ...prev, game.specId ])
      const del = game.preset?.kind === 'user_scored'
        ? await deleteUserScoredSpec({ leagueId, roundId, specId: game.specId })
        : await deleteTournamentSpec({ leagueId, roundId, specId: game.specId })
      setRemovingIds((prev) => prev.filter((id) => id !== game.specId))
      // The spec survives a failed delete (a 404, or a 422 mid spec map), so the card must stay.
      if (del.error) {
        showErrorNotification(apiError(del) || t('errors.remove_failed'))
        return
      }
    }
    // By identity, not by index: the list can be re-ordered by another removal resolving first.
    setAddedGames((prev) => prev.filter((g) => (g.specId ? g.specId !== game.specId : g !== game)))
    if (editingSpecId === game.specId) {
      setEditingSpecId(null)
      setSelectedPreset(null)
      setCustomGameMode(false)
      setCustomGameName('')
    }
  }

  const handleAddCustomGame = async () => {
    const name = customGameName.trim() || resolvedFormatName(validNodesData)
    if (!name || !customAllFilled || creating || updating || checking || resolvingNodes) { return }

    const saved = await persistSpec({
      fields: customPanelFields,
      getValue: getCustomFieldValue,
      seed: {},
      intName: name,
      addFailedKey: 'errors.add_custom_failed',
      specFields: ({ lookup, specialHoles, specAttrs }) => ({
        ...(lookup ? { lookup } : {}),
        ...(specialHoles ? { specialHoles } : {}),
        specAttrs,
      }),
    })
    if (!saved) { return }

    const { handicapAdjustment, lookup, specialHoles, specAttrs, result } = saved
    const effectiveFieldSelections = {}
    customPanelFields.forEach((f) => {
      const val = getCustomFieldValue(f.key)
      if (isPresent(val)) { effectiveFieldSelections[f.key] = val }
    })
    const resolvedOptions = {}
    Object.entries(result.data?.node_selections || {}).forEach(([ k, v ]) => {
      if (v?.id && k !== 'payout') { resolvedOptions[k] = v }
    })
    const row = {
      fieldSelections: effectiveFieldSelections, resolvedOptions, payoutTypeId, payoutRows,
      pointsRows, pointsPurseMode, handicapAdjustment, lookup, specialHoles, specAttrs,
    }

    setAddedGames((prev) => (editingSpecId
      ? prev.map((g) => (g.specId === editingSpecId
        ? { ...g, preset: { ...g.preset, id: name, name }, ...row }
        : g))
      : [ ...prev, { preset: { id: name, name, options: {} }, specId: result.data?.id, ...row } ]))
    resetCustomGamePanel()
  }

  const handleContinue = () => {
    if (!addedGames.length) { return }
    dispatch(markStepCompleted('games'))
    dispatch(goToNextStep())
  }

  if (presetsLoading) { return <LoadingState label={t('loading')}/> }
  // Only the spec list blanks the step: re-adding an unseen game is a real duplicate; add-ons repeat anyway.
  if (presetsError || specsError) { return <ErrorState message={t('error')}/> }

  const payoutSection = (idPrefix) => (
    <div className="gl-event-setup-payout-section">
      <div className="gl-event-setup-payout-title">{t('config.payout_title')}</div>
      {/* Payout type and how the amount is entered answer one question, so they share a row. */}
      <div className="gl-event-setup-payout-row">
        {payoutChoices.length > 0 && (
          <div className="gl-event-setup-payout-type-group">
            <FieldSelect
              id={`payout-type-${idPrefix}`}
              label={t('config.payout_type_label')}
              options={payoutChoices}
              value={resolvedPayoutTypeId}
              onChange={handlePayoutTypeChange}
            />
          </div>
        )}
        {!payoutHideInputs && perPlayerFeeOffered && (
          <div className="gl-event-setup-payout-type-group">
            <FieldSelect
              id={`purse-mode-${idPrefix}`}
              label={t('config.purse_mode_label')}
              options={[
                { id: 'fixed', name: t('config.purse_mode_total') },
                { id: 'dynamic', name: t('config.purse_mode_per_player') },
              ]}
              value={pointsPurseMode}
              onChange={setPointsPurseMode}
            />
          </div>
        )}
      </div>
      {!payoutHideInputs && (
        <PayoutTable
          labels={payoutFieldLabels}
          payoutRows={payoutRows}
          pointsRows={pointsRows}
          setPayoutRows={setPayoutRows}
          setPointsRows={setPointsRows}
          isExpandable={isExpandable}
          idPrefix={idPrefix}
          perPlayer={pointsPurseMode === 'dynamic'}
          showPoints={!proRataPoints}
          currencySymbol={currencySymbol}
        />
      )}
    </div>
  )

  // Under the tiles while adding, beneath the game being edited otherwise (RTM-10497).
  const primaryLabel = editingSpecId
    ? (updating ? t('actions.updating') : t('actions.update'))
    : (checking ? t('actions.checking') : (creating ? t('actions.adding') : t('actions.add')))
  const configPanel = (
    <>
      {/* ── Custom game config panel ── */}
      {customGameMode && (
      <div className="gl-event-setup-spec-config" ref={panelRef} tabIndex={-1}>
        <ConfigPanelHead
          badge="?"
          title={t('custom_game.title')}
          subtitle={t('custom_game.subtitle')}
        />
        {/* Name on its own line above the rest, the same width in every panel. */}
        <div className="gl-event-setup-sc-name-row">
          <TextField
            id="custom-game-name"
            label={t('config.name_label')}
            placeholder={t('custom_game.name_placeholder')}
            value={customGameName}
            onChange={setCustomGameName}
            onKeyDown={(event) => { if (event.key === 'Enter') { handleAddCustomGame() } }}
            autoFocus
          />
        </div>
        <ConfigFields
          fields={customPanelFields}
          idPrefix="custom-field"
          getValue={getCustomFieldValue}
          onChange={handleFieldChange}
          showAdvanced
        />
        {payoutSection('custom')}
        <SpecConfigFooter
          hint={!customAllFilled
            ? t('config.select_to_continue', { fields: customUnfilledLabels })
            : null}
        >
          <Button variant="secondary" size="sm" onClick={resetCustomGamePanel}>
            {sharedT('cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={(!customGameName.trim() && !resolvedFormatName(validNodesData)) || !customAllFilled || creating || updating || checking || resolvingNodes}
            onClick={handleAddCustomGame}
          >
            {primaryLabel}
          </Button>
        </SpecConfigFooter>
      </div>
      )}

      {/* ── Inline config ── */}
      {selectedPreset && (
      <div className="gl-event-setup-spec-config" ref={panelRef} tabIndex={-1}>
        <ConfigPanelHead
          badge={presetInitials(selectedPreset.name)}
          title={selectedPreset.name}
          subtitle={t('config.subtitle')}
        />

        {/* Locked for skins, which the engine identifies by name; no autoFocus, as the name arrives prefilled. */}
        <div
          className="gl-event-setup-sc-name-row"
          title={isSkins ? t('config.name_skins_locked') : undefined}
        >
          <TextField
            id="game-name"
            label={t('config.name_label')}
            value={gameName}
            onChange={setGameName}
            disabled={isSkins}
            onKeyDown={(event) => { if (event.key === 'Enter' && canAddGame) { handleAddGame() } }}
          />
          {!isUserScored && (
            <DisclosureButton
              row
              controls={ADVANCED_FIELDS_ID}
              expanded={advancedOpen}
              onToggle={() => setAdvancedOpen((prev) => !prev)}
              >
              {advancedOpen ? t('config.advanced_hide') : t('config.advanced_show')}
            </DisclosureButton>
          )}
        </div>
        {!isUserScored && (
          <ConfigFields
            id={ADVANCED_FIELDS_ID}
            fields={panelFields}
            idPrefix="field"
            getValue={getFieldValue}
            onChange={handleFieldChange}
            showAdvanced={advancedOpen}
          />
        )}

        {!isUserScored && payoutSection('preset')}

        <SpecConfigFooter
          hint={!canAddGame
            ? t('config.select_to_continue', {
              // The effective value, not fieldSelections, which holds only what the leader touched.
              fields: panelFields
                .filter((f) => !isFieldFilled(f, getFieldValue(f.key)))
                .map((f) => f.label)
                .join(', '),
            })
            : null}
        >
          {isUserScored && editingSpecId && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!canAddGame}
              onClick={() => handleSaveUserScored({ thenAdd: true })}
            >
              {t('actions.add_another')}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { setSelectedPreset(null); setEditingSpecId(null) }}
          >
            {sharedT('cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!canAddGame}
            onClick={handleAddGame}
          >
            {primaryLabel}
          </Button>
        </SpecConfigFooter>
      </div>
      )}
    </>
  )

  return (
    <div className="gl-event-setup-wrap">
      <PageHeader title={t('title')} subtitle={t('subtitle')}>
        <div className="gl-event-setup-filter-row">
          <div className="gl-event-setup-seg-control" role="group" aria-label={t('filter_aria_label')}>
            {[ 'individual', 'team' ].map((tab) => (
              <button
                key={tab}
                type="button"
                className={`gl-event-setup-seg-btn${formatFilter === tab ? ' is-active' : ''}`}
                aria-pressed={formatFilter === tab}
                onClick={() => dispatch(setGamesFormatFilter(tab))}
              >
                {tab === 'individual' ? t('tab_individual') : t('tab_team')}
              </button>
            ))}
          </div>

          {/* Paging sits up here: arrow columns beside the grid inset the cards from the page edge. */}
          <div className="gl-event-setup-pager">
            {[
              [ 'prev', canPrev, 'chevron-left', () => setPageIdx((i) => Math.max(0, i - 1)) ],
              [ 'next', canNext, 'chevron-right', () => setPageIdx((i) => i + 1) ],
            ].map(([ dir, enabled, icon, onClick ]) => (
              <button
                key={dir}
                type="button"
                className={`gl-event-setup-carousel-arrow${!enabled ? ' is-invisible' : ''}`}
                aria-label={t(`${dir}_aria_label`)}
                onClick={onClick}
              >
                <Icon name={icon}/>
              </button>
            ))}
          </div>
        </div>
      </PageHeader>

      {/* ── Paginated grid ── */}
      <div className="gl-event-setup-carousel-row">
        {paginated.length === 0 ? (
          <div className="gl-event-setup-grid-empty">{t('no_formats')}</div>
        ) : (
          <div className="gl-event-setup-preset-grid">
            {paginated.map((preset) => {
              const isAdded = addedIds.has(preset.id)
              const isSel = selectedPreset?.id === preset.id
              const label = pillMeta(preset).label
              const conflict = !isAdded ? getPresetConflict(preset, addedGames, { isWalkon }) : null
              // Described, not just titled: screen readers announce `title` inconsistently.
              const blockedReason = conflict || (blockedIds.has(preset.id) ? t('already_added') : null)
              const reasonId = blockedReason ? `gl-preset-${preset.id}-reason` : undefined

              return (
                <button
                  key={preset.id}
                  className={`gl-event-setup-p-card${isSel ? ' is-selected' : ''}${isAdded ? ' is-added' : ''}${conflict ? ' is-blocked' : ''}`}
                  onClick={() => handleCardClick(preset)}
                  type="button"
                  aria-pressed={isSel || isAdded}
                  aria-disabled={!!blockedReason}
                  aria-describedby={reasonId}
                  title={blockedReason || undefined}
                >
                  {isAdded && <div className="gl-event-setup-p-check" aria-hidden="true">&#10003;</div>}
                  <div className="gl-event-setup-p-card-name">{preset.name}</div>
                  <div className="gl-event-setup-p-card-sub">{label}</div>
                  {conflict && (
                    <div className="gl-event-setup-p-card-conflict">
                      <span aria-hidden="true">&#8856;</span> {t('incompatible')}
                    </div>
                  )}
                  {blockedReason && <span id={reasonId} className="sr-only">{blockedReason}</span>}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Custom game link ── */}
      {!customGameMode && (
        <div className="gl-event-setup-custom-game-row">
          <button
            type="button"
            className="gl-event-setup-custom-game-link"
            onClick={() => {
              setSelectedPreset(null)
              setCustomGameMode(true)
              resetPayoutRows()
            }}
          >
            {t('add_custom')}
          </button>
        </div>
      )}

      {/* Adding, the panel sits with the tiles; editing, it renders against the game below. */}
      {!editingSpecId && configPanel}
      {/* ── Active games ── */}
      <SectionHeader>{t('active_games_header')}</SectionHeader>

      {panelOpen && addedGames.length > 0 && (
        <div className="gl-event-setup-games-locked-note" role="status">
          {t('locked_hint')}
        </div>
      )}

      {addedGames.length === 0 ? (
        <div className="gl-event-setup-games-empty">
          <div>
            <div className="gl-event-setup-games-empty-text">{t('no_games_text')}</div>
            <div className="gl-event-setup-games-empty-sub">{t('no_games_sub')}</div>
          </div>
        </div>
      ) : (
        addedGames.map((game, idx) => {
          const summary = optSummary(game.resolvedOptions || game.preset.options || {})
          const payoutTotal = (game.payoutRows || []).reduce((sum, v) => sum + (parseFloat(v) || 0), 0)
          const pointsTotal = (game.pointsRows || []).reduce((sum, v) => sum + (parseFloat(v) || 0), 0)
          // Per-player mode stores a per-golfer fee, not the purse, so label it as a fee.
          const perPlayerFee = game.pointsPurseMode === 'dynamic'
          const feePart = [
            payoutTotal > 0 ? t(perPlayerFee ? 'entry_fee_display' : 'purse_display', { total: formatMoney(payoutTotal, currencySymbol) }) : null,
            pointsTotal > 0 ? t('points_display', { total: pointsTotal }) : null,
          ].filter(Boolean).join(' · ')
          const cardOpts = [ summary, feePart ].filter(Boolean).join(' · ')
          const isEditingThis = editingSpecId === game.specId
          const lockedTitle = panelOpen ? t('locked_hint') : undefined
          return (
            // A fragment, so the full-width panel follows its flex-row card without nesting in it.
            <React.Fragment key={game.specId ?? idx}>
              <div className={`gl-event-setup-g-card${isEditingThis ? ' is-editing' : ''}`}>
                <div className="gl-event-setup-g-card-icon" aria-hidden="true">{presetInitials(game.preset.name)}</div>
                <div className="gl-event-setup-g-card-body">
                  <div className="gl-event-setup-g-card-name">
                    {game.preset.name}
                    {isEditingThis && <Badge tone="accent">{t('editing_badge')}</Badge>}
                  </div>
                  {cardOpts && <div className="gl-event-setup-g-card-opts">{cardOpts}</div>}
                </div>
                <div className="gl-event-setup-g-card-actions">
                  {/* Every game, add-ons included, is editable: the name is (RTM-10062). */}
                  <button
                    type="button"
                    className="gl-event-setup-edit-btn"
                    onClick={() => handleEditGame(game)}
                    disabled={panelOpen}
                    title={lockedTitle}
                    aria-label={t('edit_aria_label', { name: game.preset.name })}
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="gl-event-setup-rm-btn"
                    onClick={() => handleRemoveGame(game)}
                    disabled={panelOpen || removingIds.includes(game.specId)}
                    title={lockedTitle}
                    aria-label={t('remove_aria_label', { name: game.preset.name })}
                  >
                    &times;
                  </button>
                </div>
              </div>
              {isEditingThis && (
                <div className="gl-event-setup-g-card-panel">{configPanel}</div>
              )}
            </React.Fragment>
          )
        })
      )}

      <WizardFooter
        onPrimary={handleContinue}
        primaryLabel={t('continue')}
        primaryDisabled={addedGames.length === 0}
        hint={addedGames.length === 0 ? t('hint_empty') : t('hint_count', { count: addedGames.length })}
        hintPosition="start"
      />
    </div>
  )
}

export default GamesStep
